// The colours of the brand art (brand.md §6). Every colour comes from the
// pack: the chrome palette and the accent of `[presentation.palette]` and
// `[presentation.accent]`, the three stops of the water of
// `[presentation.water]`, and the five faction colours of
// `data/identity/identity.toml`. The page writes no colour of its own.

import { FACTIONS } from '../gen/balance';
import { channels, hex, mixColor, packChannels, packHex, rgba as packedRgba } from '../render/color';
import {
  ACCENT_BRIGHT_CSS, ACCENT_CSS, PALETTE_BAD, PALETTE_GOLD, PALETTE_GOOD, PALETTE_TOKENS, SIGN,
  WATER_DEEP_LOW, WATER_DEEP_MID, WATER_DEEP_TOP,
} from '../gen/presentation';

/** A colour of `#rrggbb` from three spaced channels such as `4 15 26`. */
function hexOfChannels(channels: string): string {
  return hex(packChannels(channels));
}

/** The three channels of a colour, 0 to 255 each. */
export type RGB = readonly [number, number, number];

/** The three channels of a `#rrggbb` colour. */
export function rgbOf(hex: string): RGB {
  return channels(packHex(hex));
}

/** `rgba(r,g,b,a)` of a `#rrggbb` colour. */
export function rgba(hex: string, a: number): string {
  return packedRgba(packHex(hex), a);
}

/** The colour a share `t` (0 to 1) of the way from `a` to `b`. */
export function mix(a: string, b: string, t: number): string {
  return hex(mixColor(packHex(a), packHex(b), t));
}

/** The palette of the brand art. */
export const BRAND = {
  accent: ACCENT_CSS,
  bright: ACCENT_BRIGHT_CSS,
  text: PALETTE_TOKENS['--text'],
  dim: PALETTE_TOKENS['--dim'],
  faint: PALETTE_TOKENS['--faint'],
  panel: hexOfChannels(PALETTE_TOKENS['--panel-rgb']),
  ink: hexOfChannels(PALETTE_TOKENS['--ink-rgb']),
  deep: hexOfChannels(PALETTE_TOKENS['--deep-rgb']),
  seaTop: WATER_DEEP_TOP,
  seaMid: WATER_DEEP_MID,
  seaLow: WATER_DEEP_LOW,
  gold: PALETTE_GOLD,
  good: PALETTE_GOOD,
  bad: PALETTE_BAD,
  /** The ink of the dev corner and of the DEV line (brand.md §15). */
  amber: SIGN.dev.ink,
} as const;

/** The five faction colours, in faction order. */
export const FACTION_COLOURS: readonly string[] = FACTIONS.map((f) => hex(f.color));
